/**
 * CustomerPicker
 *
 * Modal-within-modal que permite buscar ou criar um cliente para associar
 * ao agendamento no CalendarCalendar.
 *
 * Nota: a lógica de busca/criação aqui duplica parcialmente o que
 * Customers.js faz, mas NÃO extraímos de lá por dois motivos:
 *   1. Customers.js tem contexto de detalhe/edição completo — extrair
 *      criaria acoplamento não trivial com a página pai.
 *   2. CustomerPicker é um widget autônomo e efêmero (abre/fecha),
 *      enquanto Customers.js mantém estado longo de edição e listas.
 * A duplicação é intencional e deliberada.
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import api from '../../services/api';
import { IdentityBadge } from '../ui';
import { formatPhoneBR, digitsOnly } from '../../utils/phone';
import {
  getSafeContactName,
  getSafePhone,
  getSafeUsername,
  resolveIdentityState,
} from '../../utils/identity';
import styles from './CustomerPicker.module.css';

// ── Componente ─────────────────────────────────────────────────────────────

const CustomerPicker = ({
  isOpen,
  onClose,
  onSelect,
  initialName = '',
  initialPhone = '',
}) => {
  // ─── Estado de busca ───────────────────────────────────────────────────
  const [mode, setMode] = useState('search');
  const [searchName, setSearchName] = useState(
    getSafeContactName(initialName, ''),
  );
  const [searchPhone, setSearchPhone] = useState(getSafePhone(initialPhone));
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // ─── Estado do formulário de criação ──────────────────────────────────
  const [createForm, setCreateForm] = useState({ name: '', phone: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [duplicateSuggestion, setDuplicateSuggestion] = useState(null);

  // ─── Refs para focus trap e foco automático ────────────────────────────
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const phoneInputRef = useRef(null);

  // Sincroniza props iniciais quando o picker abre
  useEffect(() => {
    if (isOpen) {
      setSearchName(getSafeContactName(initialName, ''));
      setSearchPhone(getSafePhone(initialPhone));
      setMode('search');
      setPage(1);
      setCreateForm({ name: '', phone: '' });
      setCreateError(null);
      setDuplicateSuggestion(null);
    }
  }, [isOpen, initialName, initialPhone]);

  // ─── Debounce de busca ─────────────────────────────────────────────────
  const searchTimerRef = useRef(null);

  const doSearch = useCallback(
    async (name, phone, pageNum) => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.get('/customers', {
          params: {
            ...(name ? { name } : {}),
            ...(phone ? { phone: digitsOnly(phone) } : {}),
            page: pageNum,
            limit: 20,
          },
        });
        setCustomers(res.data.customers || []);
        setPages(res.data.pages || 1);
      } catch {
        setError('Erro ao buscar clientes. Tente novamente.');
        setCustomers([]);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  // Busca inicial e ao mudar searchName/searchPhone com debounce
  useEffect(() => {
    if (!isOpen || mode !== 'search') return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    searchTimerRef.current = setTimeout(() => {
      doSearch(searchName, searchPhone, 1);
      setPage(1);
    }, 300);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [isOpen, mode, searchName, searchPhone, doSearch]);

  // Busca ao mudar de página (sem debounce)
  useEffect(() => {
    if (!isOpen || mode !== 'search') return;
    doSearch(searchName, searchPhone, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ─── Fechar com ESC ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  // ─── Focus trap ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    // Focar o botão de fechar ao abrir
    closeBtnRef.current?.focus();

    const dialog = dialogRef.current;

    const getFocusable = () =>
      Array.from(
        dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.disabled);

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    dialog.addEventListener('keydown', handleTab);
    return () => dialog.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // ─── Handlers de busca ─────────────────────────────────────────────────
  const handleSelectCustomer = (customer) => {
    onSelect(customer);
    onClose();
  };

  // ─── Handlers de criação ───────────────────────────────────────────────
  const handlePhoneInput = (e) => {
    const formatted = formatPhoneBR(e.target.value);
    setCreateForm((prev) => ({ ...prev, phone: formatted }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateError(null);
    setDuplicateSuggestion(null);

    const name = createForm.name.trim();
    const phone = digitsOnly(createForm.phone);

    if (!name || !phone) return;

    setCreateLoading(true);
    try {
      const res = await api.post('/customers', { name, phone });
      // 201 — criado com sucesso
      onSelect(res.data.customer);
      onClose();
    } catch (err) {
      if (err.response?.status === 409) {
        const existing = err.response.data?.existing;
        if (existing) {
          setDuplicateSuggestion(existing);
        } else {
          setCreateError(
            'Já existe um cliente com este telefone/conta WhatsApp.',
          );
        }
      } else {
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          'Erro ao criar cliente.';
        setCreateError(msg);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUseExisting = () => {
    if (duplicateSuggestion) {
      onSelect(duplicateSuggestion);
      onClose();
    }
  };

  const handleEditPhone = () => {
    setDuplicateSuggestion(null);
    setCreateForm((prev) => ({ ...prev, phone: '' }));
    // Focar o campo de telefone após limpar
    setTimeout(() => phoneInputRef.current?.focus(), 50);
  };

  const switchToCreate = () => {
    setMode('create');
    setCreateForm({ name: searchName, phone: '' });
    setCreateError(null);
    setDuplicateSuggestion(null);
  };

  const switchToSearch = () => {
    setMode('search');
    setCreateError(null);
    setDuplicateSuggestion(null);
  };

  if (!isOpen) return null;

  const isCreateDisabled =
    createLoading ||
    !createForm.name.trim() ||
    digitsOnly(createForm.phone).length < 10;

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div
      className={styles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-picker-title"
      >
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className={styles.header}>
          <h2 id="customer-picker-title" className={styles.title}>
            {mode === 'search' ? 'Selecionar cliente' : 'Novo cliente'}
          </h2>
          <button
            ref={closeBtnRef}
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div className={styles.body}>
          {mode === 'search' && (
            <>
              {/* Campos de busca */}
              <div className={styles.searchRow}>
                <div className={styles.searchField}>
                  <label htmlFor="picker-name" className={styles.label}>
                    Nome
                  </label>
                  <input
                    id="picker-name"
                    type="text"
                    className={styles.input}
                    placeholder="Buscar por nome"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className={styles.searchField}>
                  <label htmlFor="picker-phone" className={styles.label}>
                    Telefone compartilhado
                  </label>
                  <input
                    id="picker-phone"
                    type="tel"
                    className={styles.input}
                    placeholder="(XX) XXXXX-XXXX quando compartilhado"
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              </div>

              {/* Estados da lista */}
              {isLoading && (
                <p className={styles.loadingMsg} aria-live="polite">
                  Buscando...
                </p>
              )}

              {!isLoading && error && (
                <p className={styles.errorMsg} role="alert">
                  {error}
                </p>
              )}

              {!isLoading && !error && customers.length === 0 && (
                <div className={styles.emptyMsg} aria-live="polite">
                  <p>Nenhum cliente encontrado.</p>
                  <button
                    className={styles.btnNewCustomer}
                    onClick={switchToCreate}
                    style={{ marginTop: '0.5rem', width: 'auto', padding: '0.5rem 1.25rem' }}
                  >
                    + Criar novo cliente
                  </button>
                </div>
              )}

              {!isLoading && !error && customers.length > 0 && (
                <>
                  <ul className={styles.list} aria-label="Clientes encontrados">
                    {customers.map((c) => {
                      const displayName = getSafeContactName(
                        c.name,
                        'Cliente sem nome',
                      );
                      const safeUsername = getSafeUsername(c.username);
                      const identityState = resolveIdentityState({
                        identityState: c.identityState,
                        hasHiddenPhone: c.hasHiddenPhone,
                        phone: c.phone,
                        username: safeUsername,
                      });
                      const ariaContact =
                        identityState === 'phone' && c.phone
                          ? `telefone ${formatPhoneBR(c.phone)}`
                          : safeUsername
                            ? `nome de usuário @${safeUsername}`
                            : 'telefone não compartilhado';
                      return (
                      <li key={c.id}>
                        <button
                          className={styles.customerItem}
                          onClick={() => handleSelectCustomer(c)}
                          aria-label={`Selecionar ${displayName}, ${ariaContact}`}
                        >
                          <span className={styles.customerName}>
                            {displayName}
                          </span>
                          {identityState === 'phone' && c.phone ? (
                            <span className={styles.customerPhone}>
                              {formatPhoneBR(c.phone)}
                            </span>
                          ) : (
                            <IdentityBadge
                              state={identityState}
                              username={safeUsername}
                              phone={c.phone}
                              size="sm"
                            />
                          )}
                          {c.contacts && c.contacts.length > 0 && (
                            <div className={styles.customerBadges}>
                              {c.contacts.some(
                                (ct) => ct.platform === 'whatsapp',
                              ) && (
                                <span
                                  className={`${styles.badge} ${styles.badgeWhatsapp}`}
                                  aria-label="Canal WhatsApp"
                                >
                                  WhatsApp
                                </span>
                              )}
                              {c.contacts.some(
                                (ct) => ct.platform === 'instagram',
                              ) && (
                                <span
                                  className={`${styles.badge} ${styles.badgeInstagram}`}
                                  aria-label="Canal Instagram"
                                >
                                  Instagram
                                </span>
                              )}
                            </div>
                          )}
                        </button>
                      </li>
                      );
                    })}
                  </ul>

                  {/* Paginação */}
                  {pages > 1 && (
                    <nav
                      className={styles.pagination}
                      aria-label="Paginação de clientes"
                    >
                      <button
                        className={styles.paginationBtn}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        aria-label="Página anterior"
                      >
                        Anterior
                      </button>
                      <span>
                        {page} / {pages}
                      </span>
                      <button
                        className={styles.paginationBtn}
                        onClick={() => setPage((p) => Math.min(pages, p + 1))}
                        disabled={page === pages}
                        aria-label="Próxima página"
                      >
                        Próxima
                      </button>
                    </nav>
                  )}
                </>
              )}
            </>
          )}

          {mode === 'create' && (
            <form
              className={styles.createForm}
              onSubmit={handleCreateSubmit}
              noValidate
            >
              <div className={styles.formField}>
                <label htmlFor="create-name" className={styles.label}>
                  Nome <span aria-hidden="true">*</span>
                </label>
                <input
                  id="create-name"
                  type="text"
                  className={styles.input}
                  placeholder="Nome completo"
                  value={createForm.name}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                  autoComplete="off"
                />
              </div>

              <div className={styles.formField}>
                <label htmlFor="create-phone" className={styles.label}>
                  Telefone <span aria-hidden="true">*</span>
                </label>
                <input
                  id="create-phone"
                  ref={phoneInputRef}
                  type="tel"
                  className={styles.input}
                  placeholder="(XX) XXXXX-XXXX"
                  value={createForm.phone}
                  onChange={handlePhoneInput}
                  required
                  autoComplete="off"
                  inputMode="numeric"
                />
              </div>

              {/* Banner de duplicata (409) */}
              {duplicateSuggestion && (
                <div className={styles.duplicateBanner} role="alert">
                  <p>
                    Já existe um cliente com este telefone/conta WhatsApp:{' '}
                    <strong>{duplicateSuggestion.name}</strong>.
                  </p>
                  <div className={styles.duplicateBannerActions}>
                    <button
                      type="button"
                      className={styles.btnUseExisting}
                      onClick={handleUseExisting}
                    >
                      Usar existente
                    </button>
                    <button
                      type="button"
                      className={styles.btnEditPhone}
                      onClick={handleEditPhone}
                    >
                      Editar telefone
                    </button>
                  </div>
                </div>
              )}

              {/* Erro genérico de criação */}
              {createError && (
                <p className={styles.createError} role="alert">
                  {createError}
                </p>
              )}

              <div className={styles.createActions}>
                <button
                  type="button"
                  className={styles.btnBack}
                  onClick={switchToSearch}
                >
                  Voltar a busca
                </button>
                <button
                  type="submit"
                  className={styles.btnSubmit}
                  disabled={isCreateDisabled}
                  aria-disabled={isCreateDisabled}
                >
                  {createLoading ? 'Salvando...' : 'Criar cliente'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* ── Footer — botão "Novo cliente" sempre visível no modo search ── */}
        {mode === 'search' && customers.length > 0 && (
          <div className={styles.footer}>
            <button
              className={styles.btnNewCustomer}
              onClick={switchToCreate}
            >
              + Novo cliente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

CustomerPicker.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  initialName: PropTypes.string,
  initialPhone: PropTypes.string,
};

export default CustomerPicker;
